import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Progress } from "../ui/progress";
import { Badge } from "../ui/badge";
import { formatNumber } from "../../lib/utils";
import { format } from "date-fns";
import { TrendingUp, Users, DollarSign, Heart, Calendar } from "lucide-react";
import {
  MetricCard,
  MetricCardTitle,
  MetricCardTitleContainer,
  MoreIconButton,
  MetricAttributesContainer,
  MetricCardTopLayout,
  MetricAttribute,
  MetricAttributeText,
  ProgressBarContainer,
  ProgressBarWrapper,
  ProgressBarBackground,
  ProgressLabelContainer,
  ProgressBarLabel,
  ProgressBar,
  FloatDropdownLayout,
} from "../Metric/styles";
import { FloatingDropdown } from "../FloatingDropdown/FloatingDropdown";
import {
  VerticalMoreIcon,
} from "../../Assets/SvgIcons";
import { useDeleteBlackFundMetricMutation } from "../../../redux/reducers/apiSlice";
import toast from 'react-hot-toast';
import * as Sentry from '@sentry/react';
import { Modal } from "../Modal/Modal";
import { BlackFundMetrics } from "../../../redux/reducers/types";
import EditBlackFundImpactMetricModal from "../Modal/EditBlackFundImpactMetricModal";

interface Props {
  metric: BlackFundMetrics;
  isAdmin: boolean;
}

const confidenceLabels = {
  "1": "Not confident at all",
  "2": "Slightly confident",
  "3": "Somewhat confident",
  "4": "Confident",
  "5": "Very confident",
};

const sustainabilityColors = {
  YES: "bg-green-500",
  NO: "bg-red-500",
  UNSURE: "bg-yellow-500",
};
const BlackFundMetric: React.FC<Props> = ({ metric, isAdmin }) => {


  const confidenceLevel = parseInt(metric.increaseInFundraisingConfidence);
  const confidencePercentage = (confidenceLevel / 5) * 100;
  const [floatingDropdownVisible, setFloatingDropdownVisible] = useState(false);
  const [deleteMetricModalVisible, setDeleteMetricModalVisible] =
    useState(false);
  const [deleteMetric, { isLoading: deleteMetricLoading }] =
    useDeleteBlackFundMetricMutation();
  const [editMetricModalVisible, setEditMetricModalVisible] = useState(false);

   const handleDelete = () => {

    if (metric.goal && metric.organization) {
      try {
        deleteMetric({
          orgId: metric.organization,
          goalId: metric.goal,
          metricId: metric.id,
        }).unwrap();
        setDeleteMetricModalVisible(false);
        toast.success(`Metric deleted`);
      } catch (e) {
        Sentry.captureException(e);
        toast.error(
          `Something unexpected happened while deleting the metric. Try again later`
        );
      }
    }
  };

  const onEdit = () => {
    setEditMetricModalVisible(true);
  }

  return (
    <Card className="bg-white w-full">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-xl mb-2">Black Fund Impact Metrics</CardTitle>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar className="h-4 w-4" />
              <span>Updated {format(new Date(metric.updatedAt), "MMM dd, yyyy")}</span>
            </div>
          </div>
          {!floatingDropdownVisible && (
          <MoreIconButton
            disabled={!isAdmin}
            color={isAdmin ? "#eee" : "#000"}
            onClick={() => setFloatingDropdownVisible(true)}
          >
            <VerticalMoreIcon />
          </MoreIconButton>
        )}
        {floatingDropdownVisible && (
          <FloatDropdownLayout no_margin>
            <FloatingDropdown
              visible={floatingDropdownVisible}
              items={[
                {
                  label: "Edit",
                  action: () => (onEdit ? onEdit() : null),
                  isHighlighted: true,
                },
                // { label: "Update", action: () => setShowEditModal(true) },
                // {
                //   label: "Share as a post",
                //   action: () => setIsShareModalVisible(true),
                // },
                {
                  label: "Delete metric",
                  action: () => setDeleteMetricModalVisible(true),
                  isDestructiveAction: true,
                  isLast: true,
                },
              ]}
              onOutsideClicked={() => setFloatingDropdownVisible(false)}
            />
          </FloatDropdownLayout>
        )}
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Funding Growth */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-primary" />
            <h3 className="font-semibold">Funding Growth</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            New funding secured since receiving TBF grant
          </p>
          <div className="text-3xl font-bold text-primary">
            ${formatNumber(parseFloat(metric.newFundingSecured))}
          </div>
        </div>

        {/* Fundraising Confidence */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            <h3 className="font-semibold">Fundraising Confidence</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            {confidenceLabels[metric.increaseInFundraisingConfidence as keyof typeof confidenceLabels]}
          </p>
          <div className="space-y-2">
            <Progress value={confidencePercentage} className="h-2" />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Level {confidenceLevel}</span>
              <span>{confidencePercentage}%</span>
            </div>
          </div>
        </div>

        {/* Organizational Capacity */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            <h3 className="font-semibold">Organizational Capacity</h3>
          </div>
          <div className="grid grid-cols-3 gap-4 mt-3">
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Full-time Staff</p>
              <p className="text-2xl font-bold">{metric.fullStaff}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Part-time Staff</p>
              <p className="text-2xl font-bold">{metric.partTimeStaff}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Active Volunteers</p>
              <p className="text-2xl font-bold">{metric.activeVolunteers}</p>
            </div>
          </div>
        </div>

        {/* Organizational Sustainability */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Heart className="h-5 w-5 text-primary" />
            <h3 className="font-semibold">Organizational Sustainability</h3>
          </div>
          <p className="text-sm text-muted-foreground mb-3">
            Feeling positive about sustainability since receiving TBF funding
          </p>
          <div className="flex items-center gap-2">
            <div className={`h-3 w-3 rounded-full ${sustainabilityColors[metric.organizationalSustainability]}`} />
            <span className="font-medium">{metric.organizationalSustainability}</span>
          </div>
        </div>
      </CardContent>
      {deleteMetricModalVisible && (
      <Modal
        modalTitle="Are you sure you want to delete this black fund metric?"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        isOpen={deleteMetricModalVisible}
        onClose={() => setDeleteMetricModalVisible(false)}
        onPrimaryButtonClicked={handleDelete}
        disablePrimaryButton={deleteMetricLoading}
        primaryLoading={deleteMetricLoading}
      />
    )}
    {editMetricModalVisible && <EditBlackFundImpactMetricModal metric={metric} isOpen={editMetricModalVisible} onClose={() => setEditMetricModalVisible(false)} />}
    </Card>
  );
};


export default BlackFundMetric;